import React, { FC, useEffect, useContext, useReducer, useState } from 'react';
import _head from 'lodash/head';
import _filter from 'lodash/filter';
import _sortBy from 'lodash/sortBy';
import _compact from 'lodash/compact';
import { gqlMediaTypeCodeEnum as MEDIA_TYPE, gqlUnitSystemEnum as UNIT_SYSTEM } from '@minecraft.graphql-types';
import { useTranslation } from '@blocs.i18n';
import { helpers } from '@minecraft.utils';
import { InfoHeading } from '../styles/ProfileView';
import creditSortOrder from '../shared/utils/creditSortOrder';
import ProfileViewContext from '../ProfileViewContext';
import { MediaType } from './SummaryTypes';
import SummaryDefaultView from './SummaryDefaultView';
import SummarySubmissionDetailsView from './SummarySubmissionDetailsView';

type InitialStateType = {
  primaryPhoto: MediaType | null;
  reels: ReadonlyArray<MediaType>;
  credits: ReadonlyArray<any>;
};

type ActionType = {
  showSummaryWithSubmissionDetails: boolean;
  mediaAssets: ReadonlyArray<MediaType>;
  submissionMediaAssets: ReadonlyArray<MediaType>;
  credits: ReadonlyArray<any>;
};

const getPrimaryMedia = (files: ReadonlyArray<MediaType>, mediaTypeCode: MEDIA_TYPE): MediaType | null => {
  return _head(_filter(files, { isPrimary: true, media: { mediaType: { code: mediaTypeCode } } })) || null;
};

const profileReducer = (state: InitialStateType, action: ActionType): InitialStateType => {
  const mediaAssets = action.showSummaryWithSubmissionDetails ? action.submissionMediaAssets : action.mediaAssets;

  return {
    ...state,
    primaryPhoto: getPrimaryMedia(mediaAssets, MEDIA_TYPE.PHOTO),
    // Creates an array with all falsey values removed.
    reels: _compact([getPrimaryMedia(mediaAssets, MEDIA_TYPE.VIDEO), getPrimaryMedia(mediaAssets, MEDIA_TYPE.AUDIO)]),
    credits: creditSortOrder(_filter(action.credits, { highlighted: true })),
  };
};

const SummaryContainer: FC = () => {
  const { t } = useTranslation();

  const initialState: InitialStateType = {
    primaryPhoto: null,
    reels: [],
    credits: [],
  };
  const [{ primaryPhoto, reels, credits }, profileDispatch] = useReducer(profileReducer, initialState);
  const [showPhoto, setShowPhoto] = useState(false);
  const {
    showSummaryWithSubmissionDetails,
    showSummaryWithFolderCustomization,
    fetcher: {
      isUSCountry,
      profile,
      profileGuid,
      profileRoleId,
      submissionNote,
      overscaleNote,
      submissionMedia,
      submissionRequestedMedia,
      submissionWorkingLocation,
    },
    userName,
    userNameModalToggle,
    presentationGuid,
    presentationId,
    pickedBy,
    pickCount,
    showPicks,
  } = useContext(ProfileViewContext);

  useEffect(() => {
    profileDispatch({
      showSummaryWithSubmissionDetails,
      mediaAssets: profile.mediaAssets,
      submissionMediaAssets: submissionMedia,
      credits: profile.credits,
    });
    setShowPhoto(true);
  }, [profile]);

  const primaryWorkingLocation = _head(_sortBy(profile.workingLocations, (o) => o.sortOrder))?.name;
  const unitSystemCode = isUSCountry ? UNIT_SYSTEM.IMPERIAL : UNIT_SYSTEM.METRIC;
  const height = helpers.useProfileHeight(unitSystemCode, profile.height);
  const weight = helpers.useProfileWeight(unitSystemCode, profile.weight);

  return (
    <div id="div-summary">
      <InfoHeading>{t('common:profile.nav.summary')}</InfoHeading>
      {!showSummaryWithSubmissionDetails && (
        <SummaryDefaultView
          showPhoto={showPhoto}
          stageName={profile.stageName}
          primaryPhoto={primaryPhoto}
          talentUnions={profile.talentUnions}
          blurb={profile.blurb}
          primaryWorkingLocation={primaryWorkingLocation}
          height={height}
          weight={weight}
          credits={credits}
          reels={reels}
          userName={userName}
          userNameModalToggle={userNameModalToggle}
          presentationGuid={presentationGuid}
          presentationId={presentationId}
          pickedBy={pickedBy}
          pickCount={pickCount}
          profileId={profile.id as string}
          showPicks={showPicks}
          submissionNote={showSummaryWithFolderCustomization && submissionNote}
          overscaleNote={showSummaryWithFolderCustomization && overscaleNote}
        />
      )}
      {showSummaryWithSubmissionDetails && (
        <SummarySubmissionDetailsView
          showPhoto={showPhoto}
          isPersonal={profile?.isPersonal}
          profileMainOrganization={profile?.profileMainOrganization}
          profileMainDivision={profile?.profileMainDivision}
          stageName={profile.stageName}
          primaryPhoto={primaryPhoto}
          talentUnions={profile.talentUnions}
          blurb={profile.blurb}
          primaryWorkingLocation={submissionWorkingLocation || primaryWorkingLocation}
          height={height}
          weight={weight}
          credits={credits}
          reels={reels}
          submissionNote={submissionNote}
          submissionRequestedMedia={submissionRequestedMedia}
          profileGuid={profileGuid}
          profileRoleId={profileRoleId}
        />
      )}
    </div>
  );
};

export default SummaryContainer;
